import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../Contexts/UserContext';
import { getAuth, signOut } from 'firebase/auth';

const Navbar = () => {
  const { user, setUser } = useUser();
  const isHomePage = window.location.pathname === '/' || window.location.pathname === '/#';
  const auth = getAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const Logo = () => (
    <div className="flex items-center">
      <img src="logoctc2.png" alt="Logo" className="w-auto h-10 md:h-14 border-b-2 border-r-2 border-black" />
      <div className="flex flex-col ml-2">
        <p className="text-xs md:text-sm">Chase</p>
        <p className="text-xs md:text-sm">the</p>
        <p className="text-xs md:text-sm">Chickens</p>
      </div>
    </div>
  );

  const NavLinks = ({ mobile = false }) => {
    const baseClasses = mobile
      ? "block w-full py-2 text-gray-600 hover:text-[#E57373] transition-colors duration-200"
      : "text-gray-600 hover:text-[#E57373] transition-colors duration-200";

    return (
      <>
        {isHomePage ? (
          <>
            <a href="#hero" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>Home</a>
            <a href="#features" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>Features</a>
            <a href="#how-to-play" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>How to Play</a>
            <a href="#about-us" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>About Us</a>
          </>
        ) : (
          <>
            <Link to="/" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>Home</Link>
            <Link to="/#features" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>Features</Link>
            <Link to="/#how-to-play" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>How to Play</Link>
            <Link to="/#about-us" className={baseClasses} onClick={() => mobile && setMobileMenuOpen(false)}>About Us</Link>
          </>
        )}
      </>
    );
  };

  return (
    <nav className="bg-white/90 backdrop-blur-sm shadow-sm fixed w-full z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-2">
          {/* Logo Section */}
          {isHomePage ? (
            <a href="#hero" className="inline-flex items-center text-xl font-medium text-gray-800 font-bold mt-2">
              <Logo />
            </a>
          ) : (
            <Link to="/#hero" className="inline-flex items-center text-xl font-medium text-gray-800 font-bold mt-2">
              <Logo />
            </Link>
          )}

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-[#E57373] focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <NavLinks />
            
            {/* Auth Section */}
            <div className="flex items-center space-x-4">
              {user ? (
                <div className="relative">
                  <button
                    className="flex items-center space-x-2 focus:outline-none"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setTimeout(() => setDropdownOpen(false), 200);
                      }
                    }}
                  >
                    <UserCircle className="w-8 h-8 text-gray-600 hover:text-[#E57373]" />
                    <span className="text-gray-600">{user.username}</span>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleSignOut();
                          setDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/signin" className="text-gray-600 hover:text-[#E57373] transition-colors duration-200">
                    Sign In
                  </Link>
                  <Link to="/signup" className="bg-[#E57373] hover:scale-105 text-white rounded-full px-6 py-2 transition duration-200">
                    Sign Up
                  </Link>
                </>
              )}
              <Link to="/download" className="bg-gray-800 hover:scale-105 text-white rounded-full px-6 py-2 transition duration-200">
                Download
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-4 border-t border-gray-100">
                <div className="flex flex-col space-y-4">
                  <NavLinks mobile />
                  <div className="flex flex-col space-y-4 pt-4 border-t border-gray-100">
                    {user ? (
                      <>
                        <div className="flex items-center space-x-2 px-2">
                          <UserCircle className="w-6 h-6 text-gray-600" />
                          <span className="text-gray-600">{user.username}</span>
                        </div>
                        <Link
                          to="/profile"
                          className="block px-2 py-1 text-gray-600 hover:text-[#E57373]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Profile
                        </Link>
                        <button
                          onClick={() => {
                            handleSignOut();
                            setMobileMenuOpen(false);
                          }}
                          className="block w-full text-left px-2 py-1 text-gray-600 hover:text-[#E57373]"
                        >
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/signin"
                          className="block px-2 py-1 text-gray-600 hover:text-[#E57373]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Sign In
                        </Link>
                        <Link
                          to="/signup"
                          className="block px-2 py-1 text-gray-600 hover:text-[#E57373]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Sign Up
                        </Link>
                      </>
                    )}
                    <Link
                      to="/download"
                      className="block px-2 py-1 text-gray-600 hover:text-[#E57373]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Download
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;